import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../store';
import { getApiUrl } from '../utils/api';
import { Shield, Lock, Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';

export function PasswordPage() {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleInputChange = (field: 'currentPassword' | 'newPassword' | 'confirmPassword', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = (): boolean => {
    if (!formData.currentPassword) {
      setError(t('settings.password.validation.currentRequired'));
      return false;
    }
    
    if (!formData.newPassword) {
      setError(t('settings.password.validation.newRequired'));
      return false;
    }

    if (formData.newPassword.length < 6) {
      setError(t('settings.password.validation.minLength'));
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('settings.password.validation.mismatch'));
      return false;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError(t('settings.password.validation.sameAsCurrent'));
      return false;
    }

    return true;
  };

  const getPasswordStrength = (password: string): { strength: 'weak' | 'medium' | 'strong'; label: string; color: string } => {
    if (password.length === 0) {
      return { strength: 'weak', label: '', color: '' };
    }
    
    if (password.length < 6) {
      return { strength: 'weak', label: t('settings.password.strength.weak'), color: 'text-red-600' };
    }
    
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    const criteriaCount = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
    
    if (criteriaCount >= 3 && password.length >= 8) {
      return { strength: 'strong', label: t('settings.password.strength.strong'), color: 'text-green-600' };
    } else if (criteriaCount >= 2 || password.length >= 6) {
      return { strength: 'medium', label: t('settings.password.strength.medium'), color: 'text-yellow-600' };
    } else {
      return { strength: 'weak', label: t('settings.password.strength.weak'), color: 'text-red-600' };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!user) {
      setError(t('common.error'));
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(getApiUrl(`/api/admin/admins/${user.id}/password`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        })
      });

      if (response.ok) {
        setSuccess(t('settings.password.success.changed'));
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('settings.password.error.changeFailed'));
      }
    } catch (error) {
      console.error('Password change error:', error);
      setError(t('settings.password.error.changeFailed'));
    } finally {
      setSaving(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-textPrimary flex items-center space-x-3">
          <Shield className="w-8 h-8 text-primary-600" />
          <span>{t('settings.password.title')}</span>
          <HelpIcon 
            docPath="16-parametres#16-4-changer-le-mot-de-passe" 
            tooltip="Documentation : Changer le Mot de Passe"
          />
        </h1>
        <p className="text-textSecondary mt-2">
          {t('settings.password.subtitle')}
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center space-x-2">
          <CheckCircle className="h-5 w-5" />
          <span>{success}</span>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Password Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        <fieldset disabled={saving} className={saving ? 'opacity-60' : ''}>
        {/* Current Password Field */}
        <div>
          <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
            {t('settings.password.currentPassword')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type={showCurrentPassword ? 'text' : 'password'}
              id="currentPassword"
              value={formData.currentPassword}
              onChange={(e) => handleInputChange('currentPassword', e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('settings.password.currentPasswordPlaceholder')}
              required
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showCurrentPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* New Password Field */}
        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
            {t('settings.password.newPassword')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type={showNewPassword ? 'text' : 'password'}
              id="newPassword"
              value={formData.newPassword}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('settings.password.newPasswordPlaceholder')}
              required
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showNewPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
          {formData.newPassword && (
            <div className="mt-2">
              <div className="flex items-center space-x-2">
                <span className={`text-xs font-medium ${passwordStrength.color}`}>
                  {t('settings.password.strength.label')}: {passwordStrength.label}
                </span>
              </div>
              <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    passwordStrength.strength === 'strong' ? 'bg-green-600 w-full' :
                    passwordStrength.strength === 'medium' ? 'bg-yellow-600 w-2/3' :
                    'bg-red-600 w-1/3'
                  }`}
                />
              </div>
            </div>
          )}
        </div>

        {/* Confirm Password Field */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
            {t('settings.password.confirmPassword')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('settings.password.confirmPasswordPlaceholder')}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
          {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{t('settings.password.error.mismatch')}</p>
          )}
          {formData.confirmPassword && formData.newPassword === formData.confirmPassword && formData.newPassword.length > 0 && (
            <p className="mt-1 text-sm text-green-600 flex items-center space-x-1">
              <CheckCircle className="h-4 w-4" />
              <span>{t('settings.password.success.match')}</span>
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('settings.password.saving')}
              </>
            ) : (
              <>
                <Shield className="mr-2 h-4 w-4" />
                {t('settings.password.save')}
              </>
            )}
          </button>
        </div>
        </fieldset>
      </form>

      {/* Security Tips */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          {t('settings.password.securityTips.title')}
        </h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>{t('settings.password.securityTips.tip1')}</li>
          <li>{t('settings.password.securityTips.tip2')}</li>
          <li>{t('settings.password.securityTips.tip3')}</li>
          <li>{t('settings.password.securityTips.tip4')}</li>
          <li>{t('settings.password.securityTips.tip5')}</li>
        </ul>
      </div>
    </div>
  );
}

